import React from 'react';

import {
  CellStyleModule,
  ClientSideRowModelModule,
  GridOptions,
  RowApiModule,
  ValidationModule,
  TextFilterModule,
  RenderApiModule,
  RowSelectionModule,
  themeAlpine,
  createPart,
  CustomEditorModule,
  ExternalFilterModule,
  RowStyleModule,
  PinnedRowModule,
  TextEditorModule,
  NumberEditorModule,
  ScrollApiModule,
  HighlightChangesModule,
  ColumnApiModule,
  SelectEditorModule,
  DateEditorModule,
  AllCommunityModule,
  ClientSideRowModelApiModule,
  AlignedGridsModule
} from 'ag-grid-community';
import {
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  StatusBarModule,
  LicenseManager,
  TreeDataModule,
  ColumnsToolPanelModule,
  RowGroupingModule,
  RichSelectModule,
  CellSelectionModule
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';

import { CELL_HEIGHT } from 'app/constants/DataTrayConstants';

import { config } from 'utils/config';

LicenseManager.setLicenseKey(config.AG_GRID_KEY);

const checkboxStylePart = createPart({
  params: {
    checkboxCheckedColor: 'rgb(var(--color-cobalt-3))'
  }
});

const theme = themeAlpine.withoutPart('checkboxStyle').withPart(checkboxStylePart).withParams({
  fontFamily: 'Inter', // due to ag-grid typing, we can't use a css variable here - must be hard-coded to font name
  headerFontSize: 14,
  headerFontWeight: 'normal', // this is being overridden by the alpine theme manually imported in AdvancedGrid and ExperimentalBasicGrid, when we delete them this will work
  headerBackgroundColor: 'rgb(var(--color-light-gray-5))',
  selectedRowBackgroundColor: 'white',
  rowHoverColor: 'white',
  foregroundColor: 'rgb(var(--color-gray-2))'
});

const modules = [
  ClientSideRowModelModule,
  RowGroupingModule,
  ColumnsToolPanelModule,
  TreeDataModule,
  ServerSideRowModelModule,
  ServerSideRowModelApiModule,
  StatusBarModule,
  CellStyleModule,
  RowApiModule,
  TextFilterModule,
  PinnedRowModule,
  CustomEditorModule,
  ColumnApiModule,
  RenderApiModule,
  TextEditorModule,
  SelectEditorModule,
  DateEditorModule,
  AllCommunityModule,
  RichSelectModule,
  CellSelectionModule,
  CustomEditorModule,
  ClientSideRowModelApiModule,
  RenderApiModule,
  RowSelectionModule,
  AlignedGridsModule,
  PinnedRowModule,
  TextEditorModule,
  NumberEditorModule,
  HighlightChangesModule,
  ScrollApiModule,
  ExternalFilterModule,
  RowStyleModule,
  PinnedRowModule,
  ColumnApiModule
];

if (config.NODE_ENV === 'development') {
  modules.push(ValidationModule);
}

const DEFAULT_COL_DEFS = {
  resizable: false,
  sortable: false
};

const DEFAULT_GRID_OPTIONS: GridOptions = {
  suppressColumnVirtualisation: config.NODE_ENV === 'test',
  suppressScrollOnNewData: true,
  suppressAnimationFrame: true,
  suppressColumnMoveAnimation: true,
  suppressMovableColumns: true,
  suppressRowDrag: true,
  suppressServerSideFullWidthLoadingRow: true,
  loadingCellRenderer: 'defaultLoadingCellRenderer',
  headerHeight: CELL_HEIGHT,
  rowHeight: CELL_HEIGHT
};

// force consumers to pass grid options as props for memoization purposes
interface GridV2Props extends Omit<GridOptions, 'gridOptions'> {
  'data-testid': string;
}

const GridV2: React.FC<GridV2Props> = (props: GridV2Props) => {
  const { 'data-testid': dataTestId, defaultColDef, ...rest } = props;

  const gridOptions = {
    ...DEFAULT_GRID_OPTIONS,
    defaultColDef: defaultColDef ? { ...defaultColDef, ...DEFAULT_COL_DEFS } : DEFAULT_COL_DEFS,
    ...rest
  };
  return (
    <div className="ag-theme-alpine" data-testid={dataTestId} style={{ width: '100%', height: '100%' }}>
      <AgGridReact {...gridOptions} theme={theme} modules={modules} />
    </div>
  );
};

export default GridV2;
